import React from 'react';

import { Checkbox, Badge, Tooltip } from '@ergeon/core-components';
import { ClockIcon, UserIcon } from '@ergeon/icons/hero/outline';

import { getTitleColor, getDateColor, getIconColor, getNameColor } from '../../Activities/components/activityItemColors';

import { formatActivityDateTime, isActivityOverdue, isActivityToday } from './activityUtils';
import type { Activity } from './types';

type ActivityHeaderProps = {
  activity: Activity;
};

const ActivityHeader: React.FC<ActivityHeaderProps> = ({ activity }) => {
  const { title, due_date, due_time, completed, isImportant, user_name } = activity;

  const formattedDateTime = formatActivityDateTime(due_date, due_time, completed);
  const isOverdue = isActivityOverdue(activity);
  const isToday = isActivityToday(activity);

  const titleColor = getTitleColor(completed, isOverdue, isToday);
  const dateColor = getDateColor(completed, isOverdue, isToday);
  const iconColor = getIconColor(completed, isOverdue, isToday);
  const nameColor = getNameColor(completed, isOverdue, isToday);

  // Checkbox is read-only, onClick required by component but does nothing
  const noOp = () => { /* no-op: checkbox value is controlled by API */ };

  return (
    <div className="flex justify-between items-center gap-2 w-full">
      <div className="flex flex-1 items-center gap-2 min-w-0">
        <Checkbox
          checked={completed}
          className="pointer-events-none shrink-0"
          disabled={completed}
          onClick={noOp}
          size="small" />
        <Tooltip msg={title} position="top">
          <span className={`${titleColor} line-clamp-1 additional-4 normal-case`}>{title}</span>
        </Tooltip>
        {isImportant && !completed && <Badge className="shrink-0" flavor="danger" shape="circle" value="Important" />}
      </div>
      <div className="flex items-center gap-4 w-64 shrink-0">
        {user_name && (
          <div className="flex items-center gap-1 w-full min-w-0 max-w-32">
            <UserIcon className={`size-4 shrink-0 ${iconColor}`} />
            <span className={`${nameColor} line-clamp-1 paragraph-3 normal-case`}>{user_name}</span>
          </div>
        )}
        <div className="flex justify-end items-center gap-1 w-40">
          <ClockIcon className={`size-4 shrink-0 ${iconColor}`} />
          <span className={`${dateColor} paragraph-3 normal-case whitespace-nowrap`}>{formattedDateTime}</span>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeader;
